// Typed dates (issue 823): what somebody writes into a date field, read back as
// the ISO string the field's v-model carries — plus the plain calendar arithmetic
// the suite's date suggestions are made of. Pure TypeScript, no imports: portal-ui
// keeps a byte-identical copy at the same path (it may not import the shared library,
// ADR 0039), pinned by scripts/check-duplicate-copies.mjs.

/** `yyyy-mm-dd` → its own numbers (month 0-based), or null. Never via `Date`, which shifts the day off UTC. */
export function parseIsoDate(value: string | undefined | null): { y: number; m: number; d: number } | null {
  const parts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value || '');
  if (!parts) return null;
  const month = Number(parts[2]);
  const day = Number(parts[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  return { y: Number(parts[1]), m: month - 1, d: day };
}

/** The inverse of parseIsoDate, built from the calendar's own numbers. */
export function toIsoDate(y: number, m: number, d: number): string {
  return `${String(y).padStart(4, '0')}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

export type DateOrder = 'dmy' | 'mdy' | 'ymd';

/** In which order a locale writes day, month and year — what a typed `1/4/26` means. */
export function dateOrder(locale: string): DateOrder {
  try {
    const parts = new Intl.DateTimeFormat(locale, { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'UTC' })
      .formatToParts(new Date(Date.UTC(2026, 11, 31)))
      .map((part) => part.type)
      .filter((type) => type === 'day' || type === 'month' || type === 'year');
    const order = parts.map((type) => type[0]).join('');
    return order === 'mdy' || order === 'ymd' ? order : 'dmy';
  } catch {
    return 'dmy';
  }
}

/** The typing hint in the locale's own order and separators: `TT.MM.JJJJ`, `DD/MM/YYYY`. */
export function datePattern(locale: string, tokens: { day: string; month: string; year: string }): string {
  try {
    return new Intl.DateTimeFormat(locale, { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'UTC' })
      .formatToParts(new Date(Date.UTC(2026, 11, 31)))
      .map((part) => (part.type === 'day' ? tokens.day : part.type === 'month' ? tokens.month : part.type === 'year' ? tokens.year : part.value))
      .join('');
  } catch {
    return '';
  }
}

function daysIn(y: number, m: number): number {
  return new Date(Date.UTC(y, m + 1, 0)).getUTCDate();
}

/** `yyyy-mm-dd` plus whole months, the day clamped to the target month (31 Jan + 1 → 28/29 Feb). */
export function addMonthsIso(iso: string, months: number): string | null {
  const date = parseIsoDate(iso);
  if (!date) return null;
  const target = date.m + months;
  const y = date.y + Math.floor(target / 12);
  const m = ((target % 12) + 12) % 12;
  return toIsoDate(y, m, Math.min(date.d, daysIn(y, m)));
}

/**
 * The last day of a period of `months` that starts on `start` (§ 188 (2) BGB): six
 * months from 1 March end on 31 August, the day BEFORE the same day-of-month; a start
 * on the 31st whose target month is shorter ends on that month's last day (§ 188 (3)).
 * Probation, a fixed-term contract, an apprenticeship's planned end all count this way.
 */
export function periodEndIso(start: string, months: number): string | null {
  const date = parseIsoDate(start);
  if (!date) return null;
  const target = date.m + months;
  const y = date.y + Math.floor(target / 12);
  const m = ((target % 12) + 12) % 12;
  const last = daysIn(y, m);
  return date.d <= last ? addDaysIso(toIsoDate(y, m, date.d), -1) : toIsoDate(y, m, last);
}

/** `yyyy-mm-dd` plus whole days. */
export function addDaysIso(iso: string, days: number): string | null {
  const date = parseIsoDate(iso);
  if (!date) return null;
  const next = new Date(Date.UTC(date.y, date.m, date.d + days));
  return toIsoDate(next.getUTCFullYear(), next.getUTCMonth(), next.getUTCDate());
}

/** Today in the runtime's own calendar, as `yyyy-mm-dd`. */
export function todayIso(now: Date = new Date()): string {
  return toIsoDate(now.getFullYear(), now.getMonth(), now.getDate());
}

export interface TypedDateContext {
  order: DateOrder;
  /** What a typed date leans towards when it leaves the year (or its century) out; today by default. */
  reference: string;
  min?: string;
  max?: string;
}

function inBounds(iso: string, ctx: TypedDateContext): boolean {
  return !((ctx.min && iso < ctx.min) || (ctx.max && iso > ctx.max));
}

// A year written short (`26`, or left out) is the one nearest the reference that the
// field's own bounds allow: a birth date capped 14 years back reads `30` as 1930, a
// training end after its start reads `29` as 2029.
function resolveYear(written: number | null, digits: number, m: number, d: number, ctx: TypedDateContext): number | null {
  if (written !== null && digits >= 3) return written;
  const ref = parseIsoDate(ctx.reference) ?? parseIsoDate(todayIso())!;
  const candidates =
    written === null
      ? [ref.y, ref.y + 1, ref.y - 1]
      : [0, 1, -1].map((shift) => (Math.floor(ref.y / 100) + shift) * 100 + written);
  const valid = candidates.filter((y) => d <= daysIn(y, m));
  if (!valid.length) return null;
  const allowed = valid.filter((y) => inBounds(toIsoDate(y, m, d), ctx));
  // Without a year the reference's own year wins whenever the bounds let it; a field
  // that only takes dates after its start reads `1.4.` as the next 1 April instead.
  if (written === null && (allowed.includes(ref.y) || !allowed.length)) return valid.includes(ref.y) ? ref.y : valid[0];
  const pool = allowed.length ? allowed : valid;
  const distance = (y: number) => Math.abs(Date.UTC(y, m, d) - Date.UTC(ref.y, ref.m, ref.d));
  return [...pool].sort((a, b) => distance(a) - distance(b))[0];
}

function assemble(day: number, month: number, year: number | null, yearDigits: number, ctx: TypedDateContext): string | null {
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  const y = resolveYear(year, yearDigits, month - 1, day, ctx);
  if (y === null || y < 1 || y > 9999 || day > daysIn(y, month - 1)) return null;
  return toIsoDate(y, month - 1, day);
}

/**
 * A date as somebody types it (issue 823) → `yyyy-mm-dd`, or null when it is no date.
 *
 * Separators are free — `1.4.26`, `01/04/2026`, `1-4-2026`, `1 4 26` and `01042026` are the
 * same day — and read in the locale's order. A leading four-digit group is always ISO
 * (`2026-04-01`). The year may be short or left out (`1.4.`, `0104`); a lone number is a
 * day of the reference's month. See `resolveYear` for how a short year is completed.
 */
export function parseTypedDate(text: string, ctx: TypedDateContext): string | null {
  const raw = String(text || '').trim();
  if (!raw || /[^\d\s.,/\-–]/.test(raw)) return null;
  const groups = raw.match(/\d+/g);
  if (!groups) return null;
  const ref = parseIsoDate(ctx.reference) ?? parseIsoDate(todayIso())!;
  // The written parts in the locale's order; two parts are day and month.
  const read = (parts: string[]) => {
    const order = parts.length === 3 ? ctx.order : ctx.order.replace('y', '');
    const field: Partial<Record<'d' | 'm' | 'y', string>> = {};
    order.split('').forEach((key, i) => (field[key as 'd' | 'm' | 'y'] = parts[i]));
    if (field.d!.length > 2 || field.m!.length > 2 || (field.y !== undefined && field.y.length !== 2 && field.y.length !== 4)) return null;
    return assemble(Number(field.d), Number(field.m), field.y === undefined ? null : Number(field.y), field.y?.length ?? 0, ctx);
  };
  const iso = (parts: string[]) => assemble(Number(parts[2]), Number(parts[1]), Number(parts[0]), 4, ctx);

  if (groups.length === 1) {
    const digits = groups[0];
    if (digits.length <= 2) return assemble(Number(digits), ref.m + 1, ref.y, 4, ctx);
    if (digits.length === 4) return read([digits.slice(0, 2), digits.slice(2)]);
    if (digits.length === 6) return read([digits.slice(0, 2), digits.slice(2, 4), digits.slice(4)]);
    if (digits.length === 8) {
      const isoParts = [digits.slice(0, 4), digits.slice(4, 6), digits.slice(6)];
      if (ctx.order === 'ymd') return iso(isoParts);
      return read([digits.slice(0, 2), digits.slice(2, 4), digits.slice(4)]) ?? iso(isoParts);
    }
    return null;
  }
  if (groups.length > 3) return null;
  if (groups.length === 3 && groups[0].length === 4) return iso(groups);
  return read(groups);
}

/**
 * A typed date-time (`1.4.26 14:30`, `01.04.2026, 9`, `0104 1430`) → `yyyy-mm-ddTHH:mm`,
 * or null. A date without a time keeps `fallbackTime` (the field's previous time, or 00:00).
 */
export function parseTypedDateTime(text: string, ctx: TypedDateContext, fallbackTime = '00:00'): string | null {
  const raw = String(text || '').trim();
  if (!raw) return null;
  const iso = /^(\d{4}-\d{2}-\d{2})[T\s](\d{1,2}):(\d{2})$/.exec(raw);
  if (iso) return withTime(parseTypedDate(iso[1], ctx), Number(iso[2]), Number(iso[3]));
  const split = /^(.*?)[\s,T]+(\d{1,2})(?:[:.h]?(\d{2}))?\s*(?:uhr|h)?$/i.exec(raw);
  if (split) {
    const date = parseTypedDate(split[1], ctx);
    // `1 4 26` is a date with spaces, not 1 April at 26 o'clock: a split only counts
    // when the rest is a date of its own.
    if (date && (/[:h]/i.test(raw.slice(split[1].length)) || /\d{6,8}|\d\D+\d+\D+\d/.test(split[1]))) {
      return withTime(date, Number(split[2]), Number(split[3] ?? 0));
    }
  }
  const dateOnly = parseTypedDate(raw, ctx);
  if (!dateOnly) return null;
  const [h, m] = fallbackTime.split(':').map(Number);
  return withTime(dateOnly, h, m);
}

function withTime(date: string | null, h: number, m: number): string | null {
  if (!date || h > 23 || m > 59) return null;
  return `${date}T${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}
